/**
 * EquipmentLibrary - Panel for selecting HVAC equipment to place
 */

import React, { useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import {
  EQUIPMENT_TYPES,
  EQUIPMENT_CATEGORIES,
} from '../../constants/hvacEquipment';

export default function EquipmentLibrary({
  equipmentType,
  setEquipmentType,
  equipmentSize,
  setEquipmentSize,
  onClose,
  x = 20,
  y = 200,
  isMobile = false,
}) {
  const [minimized, setMinimized] = useState(isMobile);
  const currentEquipment = EQUIPMENT_TYPES[equipmentType] || EQUIPMENT_TYPES['furnace-gas'];
  const sizes = currentEquipment.sizes || [];

  const sizeOptions = sizes.map((size) => ({
    value: size.value,
    label: size.label,
  }));

  return (
    <FloatingPanel
      title="HVAC Equipment"
      onClose={onClose}
      onMinimize={() => setMinimized(!minimized)}
      minimized={minimized}
      isMobile={isMobile}
      x={x}
      y={y}
      maxHeight={450}
    >
      <div style={{ padding: '12px' }}>
        {/* Current Equipment Info */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            padding: '8px',
            background: `${currentEquipment.color}22`,
            borderRadius: '6px',
            border: `1px solid ${currentEquipment.color}44`,
          }}
        >
          <EquipmentIcon type={equipmentType} equipment={currentEquipment} size={32} />
          <div>
            <div style={{ fontWeight: '600', color: '#fff' }}>
              {currentEquipment.label}
            </div>
            <div style={{ fontSize: '11px', color: '#8090a0' }}>
              {sizes.find(s => s.value === equipmentSize)?.label || ''}
            </div>
          </div>
        </div>

        {Object.entries(EQUIPMENT_CATEGORIES).map(([catKey, category]) => (
          <PanelSection key={catKey} title={category.label}>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {category.types.map((typeKey) => {
                const type = EQUIPMENT_TYPES[typeKey];
                if (!type) return null;
                return (
                  <Button
                    key={typeKey}
                    variant={equipmentType === typeKey ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => {
                      setEquipmentType(typeKey);
                      setEquipmentSize(type.defaultSize);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '11px',
                      padding: '6px 8px',
                    }}
                  >
                    <EquipmentIcon type={typeKey} equipment={type} size={16} />
                    {type.label.replace('Mini Split ', '')}
                  </Button>
                );
              })}
            </div>
          </PanelSection>
        ))}

        {/* Size Selection */}
        {sizes.length > 1 && (
          <PanelSection title="Size/Capacity">
            <Select
              label="Equipment Size"
              value={equipmentSize}
              onChange={(size) => setEquipmentSize(size)}
              options={sizeOptions}
            />
          </PanelSection>
        )}

        <div
          style={{
            marginTop: '12px',
            padding: '8px',
            background: 'rgba(0,200,255,0.1)',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#8090a0',
          }}
        >
          <strong style={{ color: '#00c8ff' }}>Tip:</strong> Click on the floor plan to place. Press <strong>R</strong> to rotate.
        </div>
      </div>
    </FloatingPanel>
  );
}

// SVG icons for equipment types
function EquipmentIcon({ type, equipment, size = 40 }) {
  const { color, symbol } = equipment;
  const scale = size / 40;

  return (
    <svg width={size} height={size} viewBox="0 0 40 40">
      {/* Base shape */}
      <rect
        x="4"
        y="4"
        width="32"
        height="32"
        rx="2"
        fill={color}
        fillOpacity="0.3"
        stroke={color}
        strokeWidth="1.5"
      />

      {/* Symbol based on equipment type */}
      {symbol === 'flame' && (
        <path
          d="M20 8 C20 8 14 16 14 22 C14 26 17 30 20 30 C23 30 26 26 26 22 C26 16 20 8 20 8 Z M20 26 C18.5 26 17 24.5 17 22.5 C17 20 20 16 20 16 C20 16 23 20 23 22.5 C23 24.5 21.5 26 20 26 Z"
          fill={color}
        />
      )}

      {symbol === 'bolt' && (
        <path
          d="M22 8 L14 20 L18 20 L16 32 L26 18 L21 18 L24 8 Z"
          fill={color}
        />
      )}

      {symbol === 'fan' && (
        <>
          <circle cx="20" cy="20" r="12" fill="none" stroke={color} strokeWidth="1.5" />
          <circle cx="20" cy="20" r="3" fill={color} />
          <path
            d="M20 8 C20 8 24 12 24 16 C24 18 22 20 20 20 M32 20 C32 20 28 24 24 24 C22 24 20 22 20 20 M20 32 C20 32 16 28 16 24 C16 22 18 20 20 20 M8 20 C8 20 12 16 16 16 C18 16 20 18 20 20"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        </>
      )}

      {symbol === 'coil' && (
        <>
          <path
            d="M10 12 L30 12 L30 28 L10 28 Z"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
          />
          <path
            d="M12 16 L28 16 M12 20 L28 20 M12 24 L28 24"
            stroke={color}
            strokeWidth="1"
          />
        </>
      )}

      {symbol === 'hp' && (
        <>
          <circle cx="20" cy="20" r="12" fill="none" stroke={color} strokeWidth="1.5" />
          <text
            x="20"
            y="24"
            textAnchor="middle"
            fontSize="12"
            fontWeight="bold"
            fill={color}
          >
            HP
          </text>
        </>
      )}

      {symbol === 'head' && (
        <>
          <rect
            x="4"
            y="14"
            width="32"
            height="12"
            rx="2"
            fill={color}
            fillOpacity="0.5"
            stroke={color}
            strokeWidth="1.5"
          />
          <line x1="8" y1="18" x2="32" y2="18" stroke={color} strokeWidth="1" />
        </>
      )}

      {symbol === 'ms' && (
        <>
          <rect
            x="6"
            y="6"
            width="28"
            height="28"
            rx="2"
            fill={color}
            fillOpacity="0.3"
            stroke={color}
            strokeWidth="1.5"
          />
          <circle cx="20" cy="20" r="8" fill="none" stroke={color} strokeWidth="1" />
          <circle cx="20" cy="20" r="2" fill={color} />
        </>
      )}

      {symbol === 't' && (
        <>
          <circle
            cx="20"
            cy="20"
            r="14"
            fill={color}
            fillOpacity="0.3"
            stroke={color}
            strokeWidth="1.5"
          />
          <text
            x="20"
            y="22"
            textAnchor="middle"
            fontSize="10"
            fill={color}
          >
            72°
          </text>
        </>
      )}
    </svg>
  );
}
